import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { setTokenRefreshCallback, setAuthLogoutCallback } from "@/api/client";
import type { AuthUser } from "@/api/types";

// Google Client ID from environment variable
const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || "";

// Google Sign-In types
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          prompt: (notification?: (notification: { isNotDisplayed: () => boolean; isSkippedMoment: () => boolean }) => void) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: "standard" | "icon";
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "large" | "medium" | "small";
              text?: "signin_with" | "signup_with" | "continue_with" | "signin";
              shape?: "rectangular" | "pill" | "circle" | "square";
              logo_alignment?: "left" | "center";
              width?: number;
              locale?: string;
            }
          ) => void;
          disableAutoSelect: () => void;
          revoke: (email: string, done?: () => void) => void;
        };
      };
    };
  }
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isGoogleLoaded: boolean;
  signInWithGoogle: (buttonElement: HTMLElement) => void;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  sessionExpiredMessage: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const auth = useAuth();
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const initializedRef = useRef(false);

  // Keep a ref to the latest login function to avoid stale closure in Google callback
  const loginRef = useRef(auth.login);
  useEffect(() => {
    loginRef.current = auth.login;
  }, [auth.login]);

  // Set up token refresh and logout callbacks for API client
  useEffect(() => {
    setTokenRefreshCallback(auth.refreshToken);
  }, [auth.refreshToken]);

  useEffect(() => {
    setAuthLogoutCallback(auth.logout);
  }, [auth.logout]);

  // Initialize Google Sign-In when SDK is loaded
  const initializeGoogle = useCallback(() => {
    if (!window.google || initializedRef.current || !GOOGLE_CLIENT_ID) return;

    initializedRef.current = true;

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response) => {
        if (response.credential) {
          // Use ref to always call the latest login function
          await loginRef.current(response.credential);
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    setIsGoogleLoaded(true);
  }, []);

  // Load Google Sign-In SDK on demand
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      return;
    }

    // Check if already loaded
    if (window.google?.accounts?.id) {
      initializeGoogle();
      return;
    }

    // Check if script is already in DOM
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.onload = () => initializeGoogle();
      document.head.appendChild(script);
      return;
    }

    // Script exists but not yet loaded, poll for it
    const checkGoogleLoaded = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(checkGoogleLoaded);
        initializeGoogle();
      }
    }, 100);

    const timeout = setTimeout(() => {
      clearInterval(checkGoogleLoaded);
    }, 10000);

    return () => {
      clearInterval(checkGoogleLoaded);
      clearTimeout(timeout);
    };
  }, [initializeGoogle]);

  // Render Google Sign-In button
  const signInWithGoogle = useCallback((buttonElement: HTMLElement) => {
    if (!window.google?.accounts?.id || !isGoogleLoaded) return;

    window.google.accounts.id.renderButton(buttonElement, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: "signin_with",
      shape: "rectangular",
      logo_alignment: "left",
    });
  }, [isGoogleLoaded]);

  // Keep refs for logout to avoid stale closures
  const logoutRef = useRef(auth.logout);
  const userRef = useRef(auth.user);
  useEffect(() => {
    logoutRef.current = auth.logout;
    userRef.current = auth.user;
  }, [auth.logout, auth.user]);

  // Logout and clear Google auto-select state
  const logout = useCallback(async () => {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
    await logoutRef.current();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: auth.user,
        isAuthenticated: auth.isAuthenticated,
        isLoading: auth.isLoading,
        isGoogleLoaded,
        signInWithGoogle,
        logout,
        refreshToken: auth.refreshToken,
        sessionExpiredMessage: auth.sessionExpiredMessage,
      }}
    >
      {children}
      {/* Session Expired Toast */}
      {auth.sessionExpiredMessage && (
        <SessionExpiredToast onClose={auth.clearSessionExpiredMessage} />
      )}
    </AuthContext.Provider>
  );
}

function SessionExpiredToast({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="session-toast">
      <div className="session-toast-content">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>Session expired. Please sign in again.</span>
        <button onClick={onClose} className="session-toast-close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
